import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import {
  IBet,
  ILeg,
  IBetExtensions,
  ResultType,
  ILegResult,
  BetStatusType,
  ICanPayoutBet,
  CashOutAvailabilityReason,
  IPlacedBet,
  StateCode
} from '../types';
import { unitsValueMoney } from './money';
import { AlertType } from '../components/alert';
import { canDevicePayout, canUserCashOut } from '.';

dayjs.extend(utc);

export interface IBetStatus {
  label: IBetStatusLabel;
  icon: string | null;
  alert: IAlertType;
  canPayout: ICanPayoutBet;
  canCashOut: boolean;
  cashOutAvailabilityReason: CashOutAvailabilityReason | null;
  cashOutValue?: number;
}

export interface IBetStatusLabel {
  pos: string | null;
  posSecondary?: string;
  kiosk: string | null;
  cop?: string;
}

interface IAlertType {
  pos: AlertType;
  posSecondary?: AlertType;
  kiosk: AlertType;
}

const PromotionCashOutAvailability: CashOutAvailabilityReason[] = [
  'ANONYMOUS_BET',
  'UNSUPPORTED_REWARDS',
  'NOT_AVAILABLE_FOR_BET'
];

const defaultStatus: Pick<IBetStatus, 'canCashOut' | 'cashOutAvailabilityReason' | 'canPayout'> = {
  canCashOut: false,
  cashOutAvailabilityReason: null,
  canPayout: { cop: false, kiosk: false, pos: false }
};

export const getBetStatusConfig = ({
  cashOut,
  betStatus,
  extensions,
  type
}: Pick<IBet, 'cashOut' | 'betStatus' | 'extensions' | 'type'>): IBetStatus => {
  const canPayout = canDevicePayout();
  switch (betStatus) {
    case BetStatusType.Ungraded: {
      const {
        available: isCashOutAvailable,
        availabilityReason: cashOutAvailabilityReason,
        amount: cashOutValue,
        enabled: cashOutEnabled
      } = extensions?.cashOut || {
        available: false,
        availabilityReason: null,
        cashOutValue: undefined,
        enabled: false
      };

      const canCashOut = canUserCashOut() && isCashOutAvailable;
      const showCashOutOptions =
        canCashOut || (cashOutEnabled && PromotionCashOutAvailability.some(i => i === cashOutAvailabilityReason));
      const label = canCashOut ? null : 'Ungraded';

      return {
        ...defaultStatus,
        label: { pos: label, kiosk: label },
        icon: canCashOut ? null : 'StatusInfo',
        alert: { pos: AlertType.INFO, kiosk: AlertType.INFO },
        canCashOut,
        cashOutAvailabilityReason: showCashOutOptions ? cashOutAvailabilityReason : null,
        cashOutValue
      };
    }
    case BetStatusType.Payout: {
      return {
        ...defaultStatus,
        label: {
          pos: 'To be paid',
          kiosk: canPayout ? 'To be paid' : 'To be paid - Can only be collected at a window'
        },
        icon: cashOut ? null : 'StatusDollar',
        alert: { pos: AlertType.SUCCESS, kiosk: canPayout ? AlertType.SUCCESS : AlertType.WARNING },
        canPayout: { pos: true, kiosk: canPayout, cop: true }
      };
    }
    case BetStatusType.BlockPayoutRefund: {
      return {
        ...defaultStatus,
        label: {
          pos: 'This ticket is locked. Contact customer services',
          posSecondary: 'Voided',
          kiosk: 'Voided'
        },
        icon: 'StatusQuestionMark',
        alert: { pos: AlertType.WARNING, posSecondary: AlertType.SUCCESS, kiosk: AlertType.WARNING }
      };
    }
    case BetStatusType.BlockPayout: {
      return {
        ...defaultStatus,
        label: {
          pos: 'To be paid - This ticket is locked. Contact customer services',
          posSecondary: 'To be paid',
          kiosk: 'To be paid - Winnings must be collected at a window'
        },
        icon: 'StatusQuestionMark',
        alert: { pos: AlertType.WARNING, posSecondary: AlertType.SUCCESS, kiosk: AlertType.WARNING }
      };
    }
    case BetStatusType.PayoutTaxReportable: {
      return {
        ...defaultStatus,
        label: {
          pos: 'To be paid - Reportable winnings apply and require approval to pay out',
          posSecondary: 'To be paid - Reportable winnings apply',
          kiosk: 'To be paid - This ticket can only be collected at a window'
        },
        icon: 'StatusQuestionMark',
        alert: { pos: AlertType.WARNING, kiosk: AlertType.WARNING },
        canPayout: { pos: true, kiosk: false, cop: true }
      };
    }
    case BetStatusType.PayoutTaxWithholdings: {
      return {
        ...defaultStatus,
        label: {
          pos: 'To be paid - Regular withholdings apply and require approval to pay out',
          posSecondary: 'To be paid - Reportable winnings apply',
          kiosk: 'To be paid - This ticket can only be collected at a window'
        },
        icon: 'StatusQuestionMark',
        alert: { pos: AlertType.WARNING, kiosk: AlertType.WARNING },
        canPayout: { pos: true, kiosk: false, cop: true }
      };
    }
    case BetStatusType.PayoutWithChildSupportCheck: {
      return {
        ...defaultStatus,
        label: {
          pos: 'To be paid - CSADR check must be performed',
          posSecondary: 'To be paid',
          kiosk: 'To be paid - This ticket can only be collected at a window'
        },
        icon: 'StatusQuestionMark',
        alert: { pos: AlertType.WARNING, posSecondary: AlertType.SUCCESS, kiosk: AlertType.WARNING },
        canPayout: { pos: true, kiosk: false, cop: true }
      };
    }

    case BetStatusType.PaidViaMailPay: {
      return {
        ...defaultStatus,
        label: { pos: 'Ticket Paid by Mailpay', kiosk: 'Ticket Paid by Mailpay' },
        icon: 'StatusExclamation',
        alert: { pos: AlertType.ERROR, kiosk: AlertType.ERROR }
      };
    }

    case BetStatusType.Paid: {
      return {
        ...defaultStatus,
        label: { pos: 'Already Cashed', kiosk: 'Already Cashed' },
        icon: 'StatusExclamation',
        alert: { pos: AlertType.ERROR, kiosk: AlertType.ERROR }
      };
    }

    case BetStatusType.PushCredits: {
      return {
        ...defaultStatus,
        label: { pos: 'Push', kiosk: 'Push' },
        icon: null,
        alert: { pos: AlertType.INFO, kiosk: AlertType.INFO }
      };
    }

    case BetStatusType.Lost: {
      return {
        ...defaultStatus,
        label: { pos: 'Lost', kiosk: 'Lost' },
        icon: 'StatusCross',
        alert: { pos: AlertType.ERROR, kiosk: AlertType.ERROR }
      };
    }
    case BetStatusType.Void: {
      return {
        ...defaultStatus,
        label: { pos: 'Voided', kiosk: 'Voided' },
        icon: 'StatusQuestionMark',
        alert: { pos: AlertType.WARNING, kiosk: AlertType.WARNING },
        canPayout: { pos: true, kiosk: canPayout, cop: false }
      };
    }
    case BetStatusType.VoidCashed: {
      return {
        ...defaultStatus,
        label: { pos: 'Voided - Already Cashed', kiosk: 'Voided - Already Cashed' },
        icon: 'StatusExclamation',
        alert: { pos: AlertType.ERROR, kiosk: AlertType.ERROR }
      };
    }
    case BetStatusType.VoidCredits: {
      return {
        ...defaultStatus,
        label: { pos: 'Voided', kiosk: 'Voided' },
        icon: null,
        alert: { pos: AlertType.INFO, kiosk: AlertType.INFO }
      };
    }
    case BetStatusType.Unknown: {
      return {
        ...defaultStatus,
        label: { pos: 'Unknown', kiosk: 'Unknown' },
        icon: 'StatusQuestionMark',
        alert: { pos: AlertType.INFO, kiosk: AlertType.INFO }
      };
    }
    case BetStatusType.Expired: {
      return {
        ...defaultStatus,
        label: { pos: 'Expired - cannot be paid out', kiosk: 'Expired - cannot be paid out', cop: 'Ticket Expired' },
        icon: 'StatusClock',
        alert: { pos: AlertType.EXPIRED, kiosk: AlertType.EXPIRED },
        canPayout: {
          pos: false,
          kiosk: false,
          cop: type !== 'PARI_MUTUEL'
        }
      };
    }
    case BetStatusType.Parked: {
      return {
        ...defaultStatus,
        label: {
          pos: 'Parked - Awaiting settlement by trading. Contact trading to settle / unpark',
          posSecondary: 'Parked',
          kiosk: 'Parked - This ticket can only be collected at a window'
        },
        icon: 'StatusQuestionMark',
        alert: { pos: AlertType.WARNING, kiosk: AlertType.WARNING }
      };
    }
  }
};

export const legStatusConfig = {
  won: {
    label: 'Won',
    alert: 'success'
  },
  lost: {
    label: 'Lost',
    alert: 'error'
  },
  void: {
    label: 'Void',
    alert: 'info'
  },
  push: {
    label: 'Push',
    alert: 'info'
  },
  place: {
    label: 'Lost',
    alert: 'error'
  },
  unresult: {
    label: '-'
  }
};

enum MoneyExtensionTypes {
  REPORTABLE = 'REPORTABLE',
  WITHHOLDINGS = 'WITHHOLDINGS',
  NONE = 'NONE'
}

const getMoneyExtensionType = (extensions?: IBetExtensions): MoneyExtensionTypes =>
  extensions && extensions.money ? MoneyExtensionTypes[extensions.money.type] : MoneyExtensionTypes.NONE;

export type GetBetStatusBet = Pick<
  IBet,
  'blockPayout' | 'paidOut' | 'payout' | 'payoutExpiresAt' | 'status' | 'rewardCreditsPerLine'
> &
  Partial<Pick<IBet, 'extensions'>> &
  // payOutType is not a field available from bet-tracker
  Partial<Pick<IBet, 'payOutType'> & { legs: Pick<ILeg, 'result'>[] }>;

export const getBetStatus = (
  {
    status,
    paidOut,
    blockPayout,
    payout,
    payoutExpiresAt,
    extensions,
    payOutType,
    rewardCreditsPerLine,
    legs
  }: GetBetStatusBet,
  childSupportCheckRequired: boolean = false
) => {
  const hasBeenPaidOut = status === 'settled' && !payout.isZero() && paidOut;

  if (status === 'open') {
    return BetStatusType.Ungraded;
  }
  if (status === 'expired') {
    return BetStatusType.Expired;
  }
  if (status === 'settled' && !payout.isZero() && !paidOut) {
    const moneyExtensionType = getMoneyExtensionType(extensions);

    if (rewardCreditsPerLine && legs?.every(leg => ['push', 'void'].includes(leg?.result?.type || ''))) {
      return BetStatusType.PushCredits;
    }

    if (payoutExpiresAt && dayjs(payoutExpiresAt).isBefore(dayjs.utc())) {
      return BetStatusType.Expired;
    }
    if (blockPayout) {
      return BetStatusType.BlockPayout;
    }
    if (moneyExtensionType === MoneyExtensionTypes.REPORTABLE) {
      return BetStatusType.PayoutTaxReportable;
    }
    if (moneyExtensionType === MoneyExtensionTypes.WITHHOLDINGS) {
      return BetStatusType.PayoutTaxWithholdings;
    }
    if (childSupportCheckRequired) {
      return BetStatusType.PayoutWithChildSupportCheck;
    }

    return BetStatusType.Payout;
  }

  if (hasBeenPaidOut && payOutType === 'mailpay') {
    return BetStatusType.PaidViaMailPay;
  }

  if (hasBeenPaidOut) {
    return BetStatusType.Paid;
  }
  if (status === 'settled' && payout.isZero()) {
    return BetStatusType.Lost;
  }
  if (status === 'cancelled') {
    if (blockPayout) {
      return BetStatusType.BlockPayoutRefund;
    }
    if (rewardCreditsPerLine) {
      return BetStatusType.VoidCredits;
    }
    return paidOut ? BetStatusType.VoidCashed : BetStatusType.Void;
  }
  if (status === 'parked') {
    return BetStatusType.Parked;
  }

  return BetStatusType.Unknown;
};

export const parseBet = (bet: any): IBet => ({
  ...bet,
  totalStake: unitsValueMoney(bet.totalStake),
  potentialReturns: unitsValueMoney(bet.potentialReturns),
  originalPotentialReturns: bet.originalPotentialReturns ? unitsValueMoney(bet.originalPotentialReturns) : undefined,
  payout: unitsValueMoney(bet.payout),
  toWin: unitsValueMoney(bet.toWin),
  originalToWin: bet.originalToWin ? unitsValueMoney(bet.originalToWin) : undefined
});

export const translateBet = (bet: IBet, childSupportCheckRequired?: boolean): IBet => {
  const betStatus = getBetStatus(bet, childSupportCheckRequired);
  const { canPayout, canCashOut } = getBetStatusConfig({ ...bet, betStatus });
  return {
    ...bet,
    betStatus,
    canPayout,
    canCashOut
  };
};

export const isValidBetResultType = (legs: Pick<ILeg, 'result'>[]): boolean =>
  legs.every(({ result }: ILeg) => result === null || (Object.values(ResultType) as string[]).includes(result.type));

export const mapLegStatus = (result?: Pick<ILegResult, 'type'> | null): { label: string; alert?: string } =>
  (result && legStatusConfig[result.type]) || legStatusConfig.unresult;

export const isChildSupportCheckRequired = (
  { settled, payout, potentialReturns, totalStake }: IPlacedBet,
  stateId: StateCode
): boolean => {
  const returns = settled ? (payout as number) : potentialReturns;
  return stateId === StateCode.Indiana && returns - totalStake >= 60000;
};
